import React, { useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchTriggeredAlertsAction } from '../../store/actions/alerts';
import { resetNotificationBadge } from '../../store/actions/notificationBadge';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
  Notifications as NotificationsIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  NotificationsActive as NotificationsActiveIcon,
  NotificationsOff as NotificationsOffIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

// Pulsing animation for the Live WebSocket status
const pulse = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 8px rgba(76, 175, 80, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
`;

const exchanges = ['MCX', 'NFO', 'CDS', 'NSE', 'ALL'];

const Notifications = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState(0);
  const dispatch = useDispatch();

  // Get alerts from Redux store
  const socketMessages = useSelector((state) => state.socketMessage || {});
  const triggeredAlerts = useSelector((state) => state.alerts?.triggeredAlerts || []);
  const unseenNotifications = useSelector((state) => state.notificationBadge?.unseen || 0);

  useEffect(() => {
    dispatch(fetchTriggeredAlertsAction());
  }, [dispatch]);

  // Everything is visible on this page, so nothing stays unseen while it is open
  useEffect(() => {
    if (unseenNotifications > 0) dispatch(resetNotificationBadge());
  }, [unseenNotifications, dispatch]);

  // Process all notifications
  const allNotifications = useMemo(() => {
    const messages = [];

    const getTimestampNum = (timeVal) => {
      if (!timeVal) return Date.now();
      const d = new Date(typeof timeVal === 'number' ? (timeVal > 1e12 ? timeVal : timeVal * 1000) : timeVal);
      return Number.isNaN(d.getTime()) ? Date.now() : d.getTime();
    };

    // 1. Add triggered price alerts (Stock Alerts)
    if (triggeredAlerts && Array.isArray(triggeredAlerts)) {
      triggeredAlerts.forEach((alert) => {
        messages.push({
          id: alert.id || alert.alert_id || `triggered-${alert.update_entry_time || alert.generated_at}`,
          title: `Alert Triggered — ${alert.trading_symbol}`,
          messageText: alert.user_message || `Price alert triggered for ${alert.trading_symbol} at ${alert.new_value}`,
          messageType: 'stock',
          exchange: alert.exchange || 'ALL',
          timestamp: getTimestampNum(alert.update_entry_time || alert.generated_at),
          severity: 'info',
        });
      });
    }

    // 2. Add current messages from exchange-specific stores
    if (socketMessages.exchangeMessages) {
      exchanges.forEach((exchange) => {
        const exchangeData = socketMessages.exchangeMessages[exchange];
        if (exchangeData && exchangeData.currentMessage) {
          const msg = exchangeData.currentMessage;
          const hasContent = msg.genericMessage || msg.bodMessage || msg.holidayMessage;

          if (msg.marketStatus === 'Open' && hasContent) {
            messages.push({
              id: msg.id || `current-${exchange}-${msg.timestamp}`,
              title: `Exchange Update — ${exchange}`,
              messageText: msg.genericMessage || msg.bodMessage || msg.holidayMessage,
              messageType: 'exchange',
              exchange: exchange,
              timestamp: msg.receivedAt || msg.timestamp || Date.now(),
              marketStatus: msg.marketStatus,
              holidayMessage: msg.holidayMessage,
              currentSession: msg.currentSession,
              severity: getMessageSeverity(msg),
            });
          }
        }
      });
    }

    // Sort by timestamp descending
    return messages.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  }, [socketMessages, triggeredAlerts]);

  // Determine severity based on text content
  function getMessageSeverity(message) {
    const text = (message.genericMessage || message.bodMessage || '').toLowerCase();
    if (text.includes('error') || text.includes('failed') || text.includes('issue')) return 'error';
    if (text.includes('warning') || text.includes('caution') || text.includes('special')) return 'warning';
    if (text.includes('success') || text.includes('completed')) return 'success';
    return 'info';
  }

  // Filter messages based on active tab index: 0 = All, 1 = Exchange Updates, 2 = Stock Alerts
  const filteredNotifications = useMemo(() => {
    if (activeTab === 1) {
      return allNotifications.filter((n) => n.messageType === 'exchange');
    }
    if (activeTab === 2) {
      return allNotifications.filter((n) => n.messageType === 'stock');
    }
    return allNotifications;
  }, [allNotifications, activeTab]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getSeverityColors = (severity) => {
    switch (severity) {
      case 'error':
        return {
          border: theme.palette.error.main,
          bg: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.08)' : 'rgba(244, 67, 54, 0.04)',
          icon: <ErrorIcon color="error" />,
        };
      case 'warning':
        return {
          border: theme.palette.warning.main,
          bg: theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.08)' : 'rgba(255, 152, 0, 0.04)',
          icon: <WarningIcon color="warning" />,
        };
      case 'success':
        return {
          border: theme.palette.success.main,
          bg: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.08)' : 'rgba(76, 175, 80, 0.04)',
          icon: <CheckCircleIcon color="success" />,
        };
      default:
        return {
          border: theme.palette.info.main,
          bg: theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.08)' : 'rgba(33, 150, 243, 0.04)',
          icon: <InfoIcon color="info" />,
        };
    }
  };

  const getExchangeColor = (exchange) => {
    switch (exchange) {
      case 'SYSTEM':
        return theme.palette.warning.main;
      case 'MCX':
        return theme.palette.warning.main;
      case 'NFO':
        return theme.palette.primary.main;
      case 'CDS':
        return theme.palette.secondary.main;
      default:
        return theme.palette.info.main;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 0,
          pt: 0,
          pb: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            // maxWidth: 850,
            backgroundColor: 'transparent',
          }}
        >
          {/* Tabs Filter */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="notification tabs"
              textColor="primary"
              indicatorColor="primary"
              variant={isMobile ? 'fullWidth' : 'standard'}
            >
              <Tab label={`All (${allNotifications.length})`} sx={{ textTransform: 'none', fontWeight: 600 }} />
              <Tab
                label={`Exchange Updates (${allNotifications.filter((n) => n.messageType === 'exchange').length})`}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
              <Tab
                label={`Stock Alerts (${allNotifications.filter((n) => n.messageType === 'stock').length})`}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
            </Tabs>
          </Box>

          {/* Notifications List */}
          {filteredNotifications.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                textAlign: 'center',
              }}
            >
              <Box
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                  mb: 2,
                })}
              >
                <NotificationsOffIcon sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.5 }} />
              </Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                All Caught Up!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 350 }}>
                You have no active system or exchange alerts at the moment.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filteredNotifications.map((notif) => {
                const styles = getSeverityColors(notif.severity);
                return (
                  <Card
                    key={notif.id}
                    sx={{
                      borderLeft: `5px solid ${styles.border}`,
                      backgroundColor: styles.bg,
                      borderRadius: 2,
                      boxShadow: 'none',
                      borderTop: `1px solid ${theme.palette.divider}`,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 4px 20px rgba(0,0,0,0.4)'
                          : '0 4px 20px rgba(0,0,0,0.06)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 1.5,
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {styles.icon}
                          <Typography variant="subtitle1" fontWeight={700}>
                            {notif.title}
                          </Typography>
                        </Box>
                        <Chip
                          label={notif.exchange}
                          size="small"
                          sx={{
                            backgroundColor: getExchangeColor(notif.exchange),
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            borderRadius: '4px',
                          }}
                        />
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{
                          mb: 2,
                          lineHeight: 1.5,
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-line',
                        }}
                      >
                        {notif.messageText}
                      </Typography>

                      <Divider sx={{ my: 1, opacity: 0.5 }} />

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <ScheduleIcon sx={{ fontSize: '0.9rem' }} />
                          {formatTimestamp(notif.timestamp)}
                        </Typography>

                        {notif.currentSession && notif.currentSession.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            Session Time: <strong>{notif.currentSession[0].sessionTime}</strong>
                          </Typography>
                        )}

                        {notif.marketStatus && (
                          <Chip
                            label={`Market Status: ${notif.marketStatus}`}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              height: '20px',
                            }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default Notifications;
